import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColorOrString, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping, VariantColor } from '@repo/toolkit/colors'
import React, { PropsWithChildren, ReactNode } from 'react'

import Loader from './loader'
import Typography, { TextType } from './typography'

export type BadgeProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'color' | 'onBlur' | 'onClick' | 'style'
> & {
  text?: string | ReactNode
  textColor?: TailwindColorOrString
  variant?: VariantColor
  size?: 'xsmall' | 'small' | 'medium' | 'flexible' | 'shrink'
  textVariant?: TextType
  icon?: ReactNode
  loading?: boolean
  iconPosition?: 'start' | 'end'
  color?: string
  borderColor?: TailwindColorOrString
  className?: string
  iconClassName?: string
  textClassName?: string
  style?: React.CSSProperties
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  onBlur?: () => void
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      text,
      textColor,
      variant,
      size = 'small',
      icon,
      loading,
      iconPosition = 'end',
      textVariant = 'label-semibold',
      color,
      borderColor,
      className,
      style,
      textClassName,
      iconClassName,
      onClick,
      onBlur,
      ...props
    }: BadgeProps,
    ref
  ) => {
    const onlyIcon = !text && icon
    // since text is a react node, we need to check if it's a string or number to decide if we should wrap it in Typography
    const shouldWrapText = typeof text === 'string' || typeof text === 'number' || loading

    return (
      <div
        {...props}
        ref={ref}
        onBlur={onBlur}
        // only a clickable badge keeps the click: a plain one lets it reach its parent (e.g. the
        // row click of a table)
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation()
                onClick(e)
              }
            : undefined
        }
        className={cn(
          'w-fit flex items-center ',
          {
            'px-1 rounded-sm h-5': size === 'xsmall',
            'px-2 rounded-sm h-7': size === 'small',
            'px-3 rounded h-9': size === 'medium',
            'h-auto rounded-sm p-1': size === 'flexible',
            'px-2 white-space-wrap rounded h-fit py-1.5 min-w-[200px] max-w-[200px]':
              size === 'shrink',
            'cursor-pointer': !!onClick,
            border: variant === 'white' || borderColor
          },
          className
        )}
        style={{
          backgroundColor: color ?? colorsMapping[variant ?? 'grey'].background,
          borderColor:
            variant !== 'white'
              ? (borderColor ?? 'transparent')
              : tailwindColors.grey.light.DEFAULT,
          ...style
        }}
      >
        {icon && iconPosition === 'start' && (
          <div className={cn({ 'mr-2': !onlyIcon }, iconClassName)}>{icon}</div>
        )}

        <Typography
          as={shouldWrapText ? 'span' : undefined}
          variant={textVariant}
          color={textColor ?? colorsMapping[variant ?? 'grey'].text}
          className={textClassName}
        >
          {loading ? <Loader /> : text}
        </Typography>

        {icon && iconPosition === 'end' && <div className={cn({ 'ml-2': !onlyIcon })}>{icon}</div>}
      </div>
    )
  }
) as React.ForwardRefExoticComponent<BadgeProps> & {
  Label: React.FC<BadgeLabelProps>
  Link: React.FC<BadgeProps>
}

Badge.Link = ({ variant = 'blue', className, style, ...props }: PropsWithChildren<BadgeProps>) => (
  <Badge
    className={cn('cursor-pointer', className)}
    variant={variant}
    style={{
      textDecoration: 'underline',
      textDecorationColor: colorsMapping[variant].text,
      ...style
    }}
    {...props}
  />
)

type BadgeLabelProps = {
  label: string
  value: string | number
  icon?: ReactNode
} & BadgeProps

Badge.Label = ({ label, value, icon, ...props }: BadgeLabelProps) => (
  <Badge
    {...props}
    text={
      <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
        {label}
        <span className="text-black font-semibold">{value}</span>
      </Typography>
    }
    icon={icon}
    iconPosition="end"
  />
)

export default Badge
